import Dotdotdot from 'react-dotdotdot';
import styled, { useTheme, css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { Pill } from '@hivebrite/design-system/Pill';
import { Skeleton } from '@hivebrite/design-system/Skeleton';

import {
  getLocationValue,
  constructSubline,
} from 'hb-react/frontoffice/apps/Jobs/utils/templateUtils';
import { Link } from 'hb-react/frontoffice/components/Link';
import decodeString from 'hb-react/shared/utils/decodeString';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

export const CardJobSmallLoader = () => (
  <Card height={121} p={4}>
    <Box>
      <Skeleton height={60} width={60} mr={5} />
      <Box display="block" width="100%" css="flex: 1 1 0%;">
        <Skeleton height={24} mb={1} width="100%" />
        <Skeleton height={16} mb={3} width="40%" />
        <Skeleton height={16} mb={1} width="60%" />
        <Skeleton height={16} width="60%" />
      </Box>
    </Box>
  </Card>
);

const CardLogo = ({ logo }) => {
  const theme = useTheme();

  const size = 60;

  const borderRadiusCss = css`
    border-radius: ${theme.themeVariables.globalBorderRadius}px;
  `;

  return (
    <Box flex="0 0 auto" height={size} mr={3} width={size}>
      {logo ? (
        <Image src={logo} css={borderRadiusCss} alt="" />
      ) : (
        <Box
          p={3}
          color="white"
          css={css`
            border: 1px solid ${theme.colors.horizon};
            ${borderRadiusCss}
          `}
        >
          <FontAwesomeIcon
            iconName="briefcase"
            iconType={CATEGORIES.DUOTONE}
            height={`${size - 24}px`}
            color={theme.colors.primary}
          />
        </Box>
      )}
    </Box>
  );
};

const CardJobSmall = ({ item, to, isSelected, onClick, onCompanyPage }) => {
  const {
    locations,
    remote,
    contract_type_name: contractTypeName,
    current_user_has_already_applied: hasCurrentUserAlreadyApplied,
  } = item;

  const location = locations?.[0] || {};
  const locationValue = getLocationValue(location, remote, null, onCompanyPage);

  return (
    <Card
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        cursor: ${onClick ? 'pointer' : 'default'};
      `}
      onClick={onClick}
      onKeyDown={onEnterOrSpacePress(onClick)}
      tabIndex={onClick ? 0 : -1}
      isFocused={isSelected}
      canBeHovered
      p={0}
    >
      <OuterBox
        {...(to && {
          as: Link,
          to,
        })}
        tabIndex={to ? 0 : -1}
        data-bypass-router
      >
        <CardLogo logo={item.logo_thumb_url} />
        <Box
          flexDirection="column"
          flex="1 1 100%"
          minHeight={{ _: 48, m: 60, l: 48, xl: 72 }}
          justifyContent="center"
        >
          <Box justifyContent="space-between">
            <Dotdotdot clamp={2} useNativeClamp>
              <HeaderTitleStyled>{decodeString(item.title)}</HeaderTitleStyled>
            </Dotdotdot>
            {hasCurrentUserAlreadyApplied && (
              <Pill
                minWidth="80px"
                height="24px"
                iconName="circle-check"
                iconType={CATEGORIES.SOLID}
              >
                {s__('JobsV2|Applied')}
              </Pill>
            )}
          </Box>
          {onCompanyPage ? (
            <Box>{constructSubline(item)}</Box>
          ) : (
            <>
              <Box font="metadata-semibold-asphalt" mt={1}>
                <Dotdotdot clamp={1} useNativeClamp>
                  {decodeString(item.company_name, locationValue)}
                </Dotdotdot>
              </Box>
              {locationValue && (
                <Box font="body-regular-asphalt" fontWeight="700" mt={3}>
                  <Dotdotdot clamp={1} useNativeClamp>
                    {locationValue}
                  </Dotdotdot>
                </Box>
              )}
              {contractTypeName && (
                <Box font="body-regular-asphalt">
                  <Dotdotdot clamp={1} useNativeClamp>
                    {I18n.t(contractTypeName)}
                  </Dotdotdot>
                </Box>
              )}
            </>
          )}
        </Box>
      </OuterBox>
    </Card>
  );
};

export default CardJobSmall;

const HeaderTitleStyled = styled(Box).attrs({
  font: 'h3-semibold',
  display: 'block',
})`
  overflow-wrap: anywhere;
`;

const OuterBox = styled(Box).attrs(() => ({
  p: 4,
  flex: 1,
}))`
  border-radius: ${({ theme }) => theme.themeVariables.globalBorderRadius}px;
`;
